// components/dashboard/widgets/GraficoEvolucao.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { Spin, Empty } from 'antd';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { useDashboardData } from '../../../hooks/useDashboardData';

const GraficoEvolucao = ({ endpoint, filtros }) => {
  const { dados, loading, error } = useDashboardData(endpoint, filtros);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        padding: '20px', 
        textAlign: 'center', 
        backgroundColor: '#fff2f0',
        border: '1px solid #ffccc7',
        borderRadius: '6px',
        color: '#a8071a'
      }}>
        <p style={{ margin: '0 0 4px 0', fontWeight: 'bold' }}>
          Erro ao carregar gráfico
        </p>
        <small style={{ color: '#cf1322' }}>{error}</small>
      </div>
    );
  }

  if (!dados?.length) {
    return <Empty description="Nenhum dado encontrado" />;
  }

  // Formatar dados para o gráfico
  const dadosFormatados = dados.map(item => ({
    ...item,
    periodo: item.mes || `${item.ano}-${String(item.mes).padStart(2, '0')}`,
    mes_nome: item.mes ? new Date(2000, parseInt(item.mes.split('-')[1]) - 1).toLocaleDateString('pt-BR', { 
      month: 'short', 
      year: 'numeric' 
    }).replace('2000', item.mes.split('-')[0]) : item.mes
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip" style={{
          backgroundColor: '#fff',
          padding: '12px',
          border: '1px solid #d9d9d9',
          borderRadius: '6px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold' }}>
            {`Período: ${label}`}
          </p>
          {payload.map((entry, index) => (
            <p key={index} style={{ 
              margin: '4px 0', 
              color: entry.color 
            }}>
              {`${entry.name}: ${entry.value}`}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // PropTypes para CustomTooltip
  CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      color: PropTypes.string
    })),
    label: PropTypes.string
  };

  return (
    <div style={{ width: '100%', height: 350 }}>
      <ResponsiveContainer>
        <LineChart
          data={dadosFormatados}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis 
            dataKey="mes_nome"
            tick={{ fontSize: 12 }}
            stroke="#666"
          />
          <YAxis 
            tick={{ fontSize: 12 }}
            stroke="#666"
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Line
            type="monotone"
            dataKey="total_defesas"
            stroke="#1890ff"
            strokeWidth={3}
            dot={{ fill: '#1890ff', strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, stroke: '#1890ff', strokeWidth: 2 }}
            name="Total de Defesas"
          />
          {dadosFormatados[0]?.realizadas !== undefined && (
            <Line
              type="monotone"
              dataKey="realizadas"
              stroke="#52c41a"
              strokeWidth={2}
              dot={{ fill: '#52c41a', strokeWidth: 2, r: 4 }}
              name="Defesas Realizadas"
            />
          )}
          {dadosFormatados[0]?.agendadas !== undefined && (
            <Line
              type="monotone"
              dataKey="agendadas"
              stroke="#fa8c16"
              strokeWidth={2}
              dot={{ fill: '#fa8c16', strokeWidth: 2, r: 4 }}
              name="Defesas Agendadas"
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// PropTypes
GraficoEvolucao.displayName = 'GraficoEvolucao';
GraficoEvolucao.propTypes = {
  endpoint: PropTypes.string.isRequired,
  filtros: PropTypes.object
};

GraficoEvolucao.defaultProps = {
  filtros: {}
};

export default GraficoEvolucao;